import React, { useEffect, useState } from "react";
import { Doughnut, Pie } from "react-chartjs-2";
import "./index.scss";
import { useDispatch, useSelector } from "react-redux";
import transactionActions from "../../../redux/transactions/actions";
import actions from "../../../redux/categories/actions";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
} from "chart.js";
import { Select, Space } from "antd";

const { Option } = Select;

// Register necessary Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale
);

// Function to generate a list of colors
const generateColors = (count) => {
  const colors = [];
  const baseColors = [
    "#BFA2E5", // Light Lavender (Credit Card)
    "#FFD27F", // Soft Yellow (Cash)
    "#66D1B2", // Mint Green (Prepaid)
    "#7BAFD4", // Light Blue (Bank Transfer)
    "#F7B992", // Peach (Wallet)
    "#63D9C6", // Pastel Teal (Debit Card)
    "#AEDF8C", // Light Lime (Gift Card)
    "#809BB5", // Slate Gray-Blue (Other Payments)
    "#FFD685", // Soft Golden (Offers)
    "#82CFC6", // Pastel Aqua (Coupons)
    "#A8DFF0", // Light Sky Blue (EMI)
    "#9BAF73", // Soft Olive Green (Miscellaneous)
    "#C7E6FF", // Baby Blue (Online Payments)
  ];

  for (let i = 0; i < count; i++) {
    // Pick a color from the base set and repeat or loop over as necessary
    colors.push(baseColors[i % baseColors.length]);
  }

  return colors;
};

export const PaymentModeChart = () => {
  const { getPieChartData } = transactionActions;
  const [selectionFilter, setSelectionFilter] = useState("Amount");

  const dispatch = useDispatch();

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [], // Dynamically populated colors
      },
    ],
  });

  const { profileData } = useSelector((state) => state?.authReducer);
  const { pieChartData } = useSelector((state) => state?.transactionReducer);

  // useEffect to extract data and set it in state
  useEffect(() => {
    const paymentModeStats = Array.isArray(pieChartData?.data?.paymentModeStats)
      ? pieChartData?.data?.paymentModeStats
      : [];

    if (paymentModeStats.length > 0) {
      const paymentModes = paymentModeStats.map(
        (item) => item?.paymentMode ?? "Unknown"
      );
      const transactionPercentage = paymentModeStats.map(
        (item) => item?.transactionPercentage ?? 0
      );
      const AmountPercentage = paymentModeStats.map(
        (item) => item?.amountPercentage ?? 0
      );
      const totalAmount = paymentModeStats.map(
        (item) => item?.totalAmount / 100 ?? 0
      );
      const percentages = paymentModeStats.map((item) => item?.percentage ?? 0);
      const totalTransactions = paymentModeStats?.map(
        (item) => item?.totalTransactions
      );

      // Set the chart data
      setChartData({
        labels: paymentModes,
        datasets: [
          {
            totalTransactions: totalTransactions,
            totalAmount: totalAmount,
            data:
              selectionFilter === "Amount"
                ? AmountPercentage
                : transactionPercentage,
            backgroundColor: generateColors(paymentModes.length), // Dynamically generate colors based on the number of labels
          },
        ],
      });
    }
  }, [pieChartData?.data, selectionFilter]);

  useEffect(() => {
    if (profileData?.orgId?.length > 0) {
      dispatch(getPieChartData(profileData?.orgId));
    }
  }, [profileData?.orgId, dispatch]);

  return (
    <div className="chart-card transaction-status-card channel">
      <h6 style={{ paddingTop: "10px" }} className="pie-h1">
        Payment Mode Distribution
      </h6>
      <Space>
        <Select
          style={{ width: 100, height: 20 }}
          value={selectionFilter}
          onChange={(value) => {
            setSelectionFilter(value);
          }}
        >
          <Option value="Amount">Amount</Option>
          <Option value="Transactions">Transactions</Option>
        </Select>
      </Space>
      <div
        style={{
          width: "283px",
          height: "280px",
          marginTop: "5px",
          paddingRight: "10px",
        }}
      >
        <Doughnut
          data={chartData}
          options={{
            cutout: "65%",
            responsive: true,
            plugins: {
              tooltip: {
                callbacks: {
                  label: (tooltipItem) =>
                    `${tooltipItem.label}: ${tooltipItem.raw.toFixed(2)}%`,
                },
              },
              legend: {
                position: "bottom",
                labels: {
                  usePointStyle: true,
                  font: { size: 12 },
                  padding: 12,
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
};

// Transaction Status Chart Component
export const TransactionStatusChart = () => {
  const { pieChartData } = useSelector((state) => state.transactionReducer);

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [], // Dynamically populated colors
      },
    ],
  });

  useEffect(() => {
    const statusStats = Array.isArray(pieChartData?.data?.statusStats)
      ? pieChartData?.data?.statusStats
      : [];

    if (statusStats.length > 0) {
      const statuses = statusStats.map((item) => item?.status ?? "Unknown");
      const percentages = statusStats.map((item) => item?.percentage ?? 0);

      const statusColors = statuses.map((status) => {
        switch (status) {
          case "Successful":
            return "rgba(172, 219, 118, 0.6)";
          case "Pending":
            return "rgba(251, 119, 79, 0.6)";
          case "Failed":
            return " rgba(253, 51, 95, 0.6)";
          default:
            return "#007bff";
        }
      });

      setChartData({
        labels: statuses,
        datasets: [
          {
            data: percentages,
            backgroundColor: statusColors,
          },
        ],
      });
    }
  }, [pieChartData?.data]);

  return (
    <div className="chart-card transaction-status-card">
      <h5 style={{ textAlign: "center", paddingTop: "10px" }}>
        Transaction Rate
      </h5>
      <div
        id="txnStatusChart"
        style={{
          width: "237px",
          height: "220px",
          paddingLeft: "16px",
          margin: "10px auto",
        }}
      >
        <Doughnut
          data={chartData}
          options={{
            cutout: "65%",
            responsive: true,
            plugins: {
              legend: {
                position: "bottom",
                labels: {
                  usePointStyle: true,
                  font: { size: 12 },
                  padding: 6,
                },
                align: "center", // 👈 centers legend
              },
            },
          }}
        />
      </div>
    </div>
  );
};

// export const CategoryModeChart = () => {
//   const profileData = useSelector((state) => state.authReducer.profileData);
//   const { categoryDetails = [], loading } = useSelector(
//     (state) => state?.categoryReducer || {}
//   );

//   const [selectedCategory, setSelectedCategory] = useState(null);
//   const [chartData, setChartData] = useState(null);
//   const dispatch = useDispatch();

//   // fetch categories on mount
//   useEffect(() => {
//     if (profileData?.orgId) {
//       dispatch(actions.getCategoryDetails(profileData.orgId));
//     }
//   }, [profileData?.orgId, dispatch]);

//   // set first category after API loads
//   useEffect(() => {
//     if (categoryDetails.length > 0 && !selectedCategory) {
//       setSelectedCategory(categoryDetails[0]);
//     }
//   }, [categoryDetails, selectedCategory]);

//   // build chartData when category changes
//   useEffect(() => {
//     if (selectedCategory) {
//       setChartData({
//         labels: ["Total Amount", "Total Transactions", "Agent Commission"],
//         datasets: [
//           {
//             data: [
//               (selectedCategory.totalTransactionAmount || 0) / 100,
//               selectedCategory.totalTransactionCount || 0,
//               selectedCategory.totalAgentCommission || 0,
//             ],
//             backgroundColor: chartColors,
//           },
//         ],
//       });
//     }
//   }, [selectedCategory]);

//   // custom tooltip
//   const externalTooltipHandler = (context) => {
//     const { chart, tooltip } = context;
//     let tooltipEl = document.getElementById("chartjs-tooltip");

//     if (!tooltipEl) {
//       tooltipEl = document.createElement("div");
//       tooltipEl.id = "chartjs-tooltip";
//       document.body.appendChild(tooltipEl);
//     }

//     if (tooltip.opacity === 0) {
//       tooltipEl.style.opacity = "0";
//       return;
//     }

//   if (tooltip.body && selectedCategory) {
//       const tooltipEl = document.getElementById('chartjs-tooltip');

//       // Create HTML for all three metrics with colors
//       const html = `
//         <div style="padding: 12px; background: white; border: 1px solid #ddd; border-radius: 6px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
//           <div style="display: flex; align-items: center; margin-bottom: 8px;">
//             <div style="width: 14px; height: 14px; background: ${chartColors[0]}; margin-right: 10px; border-radius: 3px;"></div>
//             <span style="font-size: 13px;">Total Amount: ₹${selectedCategory.totalTransactionAmount/100}</span>
//           </div>
//           <div style="display: flex; align-items: center; margin-bottom: 8px;">
//             <div style="width: 14px; height: 14px; background: ${chartColors[1]}; margin-right: 10px; border-radius: 3px;"></div>
//             <span style="font-size: 13px;">Total Transactions: ${selectedCategory.totalTransactionCount}</span>
//           </div>
//           <div style="display: flex; align-items: center;">
//             <div style="width: 14px; height: 14px; background: ${chartColors[2]}; margin-right: 10px; border-radius: 3px;"></div>
//             <span style="font-size: 13px;">Total Commission: ₹${selectedCategory.totalAgentCommission}</span>
//           </div>
//         </div>
//       `;

//       tooltipEl.innerHTML = html;

//       // Position the tooltip
//       const position = chart.canvas.getBoundingClientRect();
//       tooltipEl.style.position = 'absolute';
//       tooltipEl.style.background = 'transparent';
//       tooltipEl.style.pointerEvents = 'none';
//       tooltipEl.style.left = position.left + window.scrollX + tooltip.caretX + 'px';
//       tooltipEl.style.top = position.top + window.scrollY + tooltip.caretY + 'px';
//       tooltipEl.style.opacity = '1';
//       tooltipEl.style.transition = 'opacity 0.3s';
//       tooltipEl.style.zIndex = '9999';
//     }

//     const position = chart.canvas.getBoundingClientRect();
//     tooltipEl.style.position = "absolute";
//     tooltipEl.style.pointerEvents = "none";
//     tooltipEl.style.left = position.left + window.scrollX + tooltip.caretX + "px";
//     tooltipEl.style.top = position.top + window.scrollY + tooltip.caretY + "px";
//     tooltipEl.style.opacity = "1";
//     tooltipEl.style.transition = "opacity 0.3s";
//     tooltipEl.style.zIndex = "9999";
//   };

//   return (
//     <div style={{ width: "100%", maxWidth: "600px", margin: "0 auto"}}>
//       <h5 style={{ textAlign: "center" }}>Category Mode Distribution</h5>

//       <div style={{ width: "250px", height: "250px", margin: "0 auto" }}>
//         {chartData && (
//           <Doughnut
//             data={chartData}
//             options={{
//               cutout: "65%",
//               responsive: true,
//               plugins: {
//                 tooltip: {
//                   enabled: false,
//                   external: externalTooltipHandler,
//                 },
//                 legend: { display: false },
//               },
//             }}
//           />
//         )}
//       </div>

//       {/* Category selectors */}
//       <div
//         style={{
//           display: "flex",
//           flexWrap: "wrap",
//           gap: "8px",
//           marginTop: "15px",
//           justifyContent: "center",
//         }}
//       >
//         {categoryDetails.map((cat) => (
//           <div
//             key={cat.categoryName}
//             onClick={() => setSelectedCategory(cat)}
//             style={{
//               padding: "4px 10px",
//               borderRadius: "20px",
//               cursor: "pointer",
//               border:
//                 selectedCategory?.categoryName === cat.categoryName
//                   ? "2px solid #2f2ffaff"
//                   : "1px solid #ddd",
//               backgroundColor:
//                 selectedCategory?.categoryName === cat.categoryName
//                   ? "#f04affff"
//                   : "#fff",
//               color:
//                 selectedCategory?.categoryName === cat.categoryName
//                   ? "#fff"
//                   : "#000",
//             }}
//           >
//             <span style={{ fontSize: "12px" }}>{cat.categoryName}</span>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };

export const CategoryModeChart = () => {
  const profileData = useSelector((state) => state.authReducer.profileData);
  const { categoryDetails = [] } = useSelector(
    (state) => state?.categoryReducer || {}
  );

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [chartData, setChartData] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (profileData?.orgId) {
      dispatch(actions.getCategoryDetails(profileData.orgId));
    }
  }, [profileData?.orgId, dispatch]);

  useEffect(() => {
    if (!selectedCategory && categoryDetails.length > 0) {
      setChartData({
        labels: categoryDetails.map((cat) => cat.categoryName),
        datasets: [
          {
            data: categoryDetails.map((cat) => cat.totalTransactionCount || 0),
            backgroundColor: generateColors(categoryDetails.length),
          },
        ],
      });
    } else if (selectedCategory) {
      setChartData({
        labels: ["Total Amount", "Total Transactions", "Agent Commission"],
        datasets: [
          {
            data: [
              (selectedCategory.totalTransactionAmount || 0) / 100,
              selectedCategory.totalTransactionCount || 0,
              selectedCategory.totalAgentCommission || 0,
            ],
            backgroundColor: chartColors,
          },
        ],
      });
    }
  }, [selectedCategory, categoryDetails]);

  const externalTooltipHandler = (context) => {
    const { chart, tooltip } = context;
    let tooltipEl = document.getElementById("chartjs-tooltip");
    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.id = "chartjs-tooltip";
      document.body.appendChild(tooltipEl);
    }
    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = "0";
      return;
    }

    if (tooltip.body) {
      let html = "";
      if (!selectedCategory) {
        const colors = chart.data.datasets[0].backgroundColor;
        tooltip.dataPoints.forEach((dp) => {
          html += `
            <div style="display:flex;align-items:center;margin-bottom:6px;">
              <div style="width:14px;height:14px;background:${
                colors[dp.dataIndex]
              };margin-right:8px;border-radius:3px;"></div>
              <span style="font-size:13px;">
                ${dp.label}: ${dp.raw} total transactions
              </span>
            </div>`;
        });
        html = `<div style="padding:12px;background:white;border:1px solid #ddd;border-radius:6px;box-shadow:0 2px 10px rgba(0,0,0,0.1);">${html}</div>`;
      } else {
        html = `
          <div style="padding:12px;background:white;border:1px solid #ddd;border-radius:6px;box-shadow:0 2px 10px rgba(0,0,0,0.1);">
            <div style="display:flex;align-items:center;margin-bottom:8px;">
              <div style="width:14px;height:14px;background:${
                chartColors[0]
              };margin-right:10px;border-radius:3px;"></div>
              <span style="font-size:13px;">Total Amount: ₹${
                (selectedCategory.totalTransactionAmount || 0) / 100
              }</span>
            </div>
            <div style="display:flex;align-items:center;margin-bottom:8px;">
              <div style="width:14px;height:14px;background:${
                chartColors[1]
              };margin-right:10px;border-radius:3px;"></div>
              <span style="font-size:13px;">Total Transactions: ${
                selectedCategory.totalTransactionCount || 0
              }</span>
            </div>
            <div style="display:flex;align-items:center;">
              <div style="width:14px;height:14px;background:${
                chartColors[2]
              };margin-right:10px;border-radius:3px;"></div>
              <span style="font-size:13px;">Total Commission: ₹${
                selectedCategory.totalAgentCommission || 0
              }</span>
            </div>
          </div>`;
      }

      tooltipEl.innerHTML = html;
      const position = chart.canvas.getBoundingClientRect();
      tooltipEl.style.position = "absolute";
      tooltipEl.style.pointerEvents = "none";
      tooltipEl.style.left =
        position.left + window.scrollX + tooltip.caretX + "px";
      tooltipEl.style.top =
        position.top + window.scrollY + tooltip.caretY + "px";
      tooltipEl.style.opacity = "1";
      tooltipEl.style.transition = "opacity 0.3s";
      tooltipEl.style.zIndex = "9999";
    }
  };

  return (
    <div style={{ width: "100%", maxWidth: "600px", margin: "20px auto" }}>
      <h5 style={{ textAlign: "center", marginBottom: 16 }}>
        Category Mode Distribution
      </h5>

      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <Select
          allowClear
          placeholder="Select Category"
          style={{ width: 250 }}
          value={selectedCategory?.categoryName}
          onChange={(value) => {
            const cat = categoryDetails.find((c) => c.categoryName === value);
            setSelectedCategory(cat || null);
          }}
        >
          {categoryDetails
            .filter((c) => c?.categoryName)
            .map((cat) => (
              <Select.Option key={cat.categoryName} value={cat.categoryName}>
                {cat.categoryName}
              </Select.Option>
            ))}
        </Select>
      </div>

      <div style={{ width: "400px", height: "400px", margin: "0 auto" }}>
        {chartData && (
          <Doughnut
            data={chartData}
            options={{
              cutout: "65%",
              responsive: true,
              plugins: {
                tooltip: { enabled: false, external: externalTooltipHandler },
                legend: { display: false },
              },
            }}
          />
        )}
      </div>
    </div>
  );
};

const chartColors = ["#9ae2f8ff", "#AEDF8C", "#FFD27F"];
